/**
 * Square Data Mapping — static field reference guide.
 *
 * This is a read-only reference component (no API calls). It shows merchants
 * which Square data fields are available, their variable paths for workflow use,
 * and example values. Unlike Shopify's configurable mapping panel, Square data
 * is surfaced via AI skills that read raw JSON — no schema config needed.
 *
 * Scopes: Booking, Customer, Order, CatalogItem, Loyalty
 */
import React, { useState } from "react";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

// ─── Field table ──────────────────────────────────────────────────────────────

interface FieldDef {
  field: string;
  description: string;
  variable: string;
  example: string;
  isArray?: boolean;
}

function FieldTable({ scope, fields }: { scope: string; fields: FieldDef[] }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden text-xs mb-4">
      <table className="w-full">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-1/4">Field</th>
            <th className="px-3 py-2 text-left font-medium w-1/4">Description</th>
            <th className="px-3 py-2 text-left font-medium w-1/3">Variable path</th>
            <th className="px-3 py-2 text-left font-medium">Example</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {fields.map((f, i) => (
            <tr key={f.field} className={i % 2 === 1 ? "bg-slate-50" : ""}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  <code className="text-primary-active font-mono">{f.field}</code>
                  {f.isArray && (
                    <span className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">array</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-slate-600">{f.description}</td>
              <td className="px-3 py-2">
                <code className="text-[10px] text-slate-500 font-mono break-all">
                  {`{square.${scope}.${f.field}}`}
                </code>
              </td>
              <td className="px-3 py-2 text-slate-500 italic">{f.example}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Scope definitions ────────────────────────────────────────────────────────

const BOOKING_FIELDS: FieldDef[] = [
  { field: "booking_id",               description: "Unique booking identifier",                    variable: "booking_id",               example: "v1:booking:abc123" },
  { field: "start_at",                 description: "ISO 8601 start time (UTC)",                   variable: "start_at",                 example: "2026-04-10T14:00:00Z" },
  { field: "end_at",                   description: "ISO 8601 end time (UTC)",                     variable: "end_at",                   example: "2026-04-10T15:00:00Z" },
  { field: "status",                   description: "Booking status",                              variable: "status",                   example: "ACCEPTED" },
  { field: "created_at",               description: "When the booking was created",                variable: "created_at",               example: "2026-04-01T09:30:00Z" },
  { field: "service_variation_id",     description: "ID of the booked service variation",          variable: "service_variation_id",     example: "S7ABCDEF12345" },
  { field: "service_variation_name",   description: "Name of the service",                         variable: "service_variation_name",   example: "Haircut & Style" },
  { field: "service_duration_minutes", description: "Duration of the service in minutes",          variable: "service_duration_minutes", example: "60" },
  { field: "team_member_id",           description: "Staff member ID",                             variable: "team_member_id",           example: "TMBrOwNHaiRCuT" },
  { field: "team_member_name",         description: "Staff member display name",                   variable: "team_member_name",         example: "Alex Smith" },
  { field: "customer_id",              description: "Square customer ID linked to the booking",    variable: "customer_id",              example: "JDKYHBWT1D4F8MFH63DBMEN8Y4" },
  { field: "customer_note",            description: "Customer-provided booking note",              variable: "customer_note",            example: "Booked via Velaro chat" },
  { field: "location_id",              description: "Square location where appointment is booked", variable: "location_id",              example: "L1234ABCDE" },
];

const CUSTOMER_FIELDS: FieldDef[] = [
  { field: "id",           description: "Square customer ID (unique)",                variable: "id",           example: "JDKYHBWT1D4F8MFH63DBMEN8Y4" },
  { field: "given_name",   description: "First name",                                variable: "given_name",   example: "Jane" },
  { field: "family_name",  description: "Last name",                                 variable: "family_name",  example: "Smith" },
  { field: "email_address",description: "Primary email address",                     variable: "email_address",example: "jane@example.com" },
  { field: "phone_number", description: "Primary phone (E.164 format)",              variable: "phone_number", example: "+15551234567" },
  { field: "reference_id", description: "External reference (your system's ID)",     variable: "reference_id", example: "CRM-00412" },
  { field: "note",         description: "Internal note set by merchant",             variable: "note",         example: "VIP client, prefers Sarah" },
  { field: "birthday",     description: "Customer birthday (YYYY-MM-DD)",            variable: "birthday",     example: "1990-06-15" },
  { field: "created_at",   description: "When the customer record was created",      variable: "created_at",   example: "2024-01-20T10:00:00Z" },
  { field: "updated_at",   description: "Last time the record was updated",          variable: "updated_at",   example: "2026-03-01T08:45:00Z" },
  { field: "group_ids",    description: "Group memberships (e.g. VIP, Wholesale)",  variable: "group_ids",    example: "[\"GROUP_VIPS\"]", isArray: true },
];

const ORDER_FIELDS: FieldDef[] = [
  { field: "id",               description: "Order ID",                           variable: "id",               example: "CAISENgvlLXpdFnC..." },
  { field: "state",            description: "Order lifecycle state",              variable: "state",            example: "COMPLETED" },
  { field: "created_at",       description: "When the order was placed",          variable: "created_at",       example: "2026-03-20T14:22:00Z" },
  { field: "updated_at",       description: "Last update timestamp",             variable: "updated_at",       example: "2026-03-20T15:10:00Z" },
  { field: "fulfillment_type", description: "How the order will be fulfilled",   variable: "fulfillment_type", example: "PICKUP" },
  { field: "line_items",       description: "Array of line items in the order",  variable: "line_items",       example: "[{name, quantity, base_price_money}]", isArray: true },
  { field: "total_money",      description: "Total order amount (amount + currency)", variable: "total_money", example: "{amount: 2500, currency: 'USD'}" },
  { field: "customer_id",      description: "Square customer who placed the order", variable: "customer_id",   example: "JDKYHBWT1D4F8MFH63DBMEN8Y4" },
  { field: "location_id",      description: "Location where the order was placed", variable: "location_id",   example: "L1234ABCDE" },
];

const CATALOG_FIELDS: FieldDef[] = [
  { field: "id",          description: "Catalog item ID",                          variable: "id",          example: "MNSY6YQTMJ3SBMYB3JTFNQFZ" },
  { field: "name",        description: "Item display name",                        variable: "name",        example: "Blue Denim Jacket" },
  { field: "description", description: "Full item description",                   variable: "description", example: "Classic fit, 100% cotton..." },
  { field: "category_id", description: "Parent category ID",                      variable: "category_id", example: "CAT_OUTERWEAR_123" },
  { field: "variations",  description: "Array of item variations (size, color, price, SKU)", variable: "variations", example: "[{name:'S', price_money:{amount:4999}}]", isArray: true },
  { field: "is_taxable",  description: "Whether the item is subject to tax",      variable: "is_taxable",  example: "true" },
  { field: "tax_ids",     description: "Applied tax IDs",                         variable: "tax_ids",     example: "[\"TAX_STATE_GA\"]", isArray: true },
  { field: "image_ids",   description: "Associated image IDs",                    variable: "image_ids",   example: "[\"IMG_ABC123\"]", isArray: true },
];

const LOYALTY_FIELDS: FieldDef[] = [
  { field: "id",             description: "Loyalty account ID",                  variable: "id",             example: "79b807d2-d786-46a9-933b-918028d7a8c" },
  { field: "program_id",     description: "Loyalty program ID",                  variable: "program_id",     example: "d8fcf9e0-bf5f-4ef8-9d56-9a9d65bed657" },
  { field: "balance",        description: "Current point balance",               variable: "balance",        example: "150" },
  { field: "lifetime_points",description: "Total points earned all time",        variable: "lifetime_points",example: "420" },
  { field: "customer_id",   description: "Square customer linked to this account",variable: "customer_id",  example: "JDKYHBWT1D4F8MFH63DBMEN8Y4" },
  { field: "created_at",    description: "When the loyalty account was created", variable: "created_at",    example: "2024-03-15T12:00:00Z" },
  { field: "enrolled_at",   description: "When the customer enrolled",           variable: "enrolled_at",   example: "2024-03-15T12:00:00Z" },
  { field: "status",        description: "Account status",                       variable: "status",        example: "ACTIVE" },
];

// ─── Scope tabs ───────────────────────────────────────────────────────────────

type ScopeId = "booking" | "customer" | "order" | "catalog" | "loyalty";

const SCOPES: { id: ScopeId; label: string; icon: string; description: string }[] = [
  { id: "booking",  label: "Booking",     icon: "fa-calendar-check", description: "Appointment booking data from Square Bookings API" },
  { id: "customer", label: "Customer",    icon: "fa-user",            description: "Customer profile data from Square Customers API" },
  { id: "order",    label: "Order",       icon: "fa-bag-shopping",    description: "Retail order data from Square Orders API" },
  { id: "catalog",  label: "Catalog Item",icon: "fa-tag",             description: "Product/service catalog data from Square Catalog API" },
  { id: "loyalty",  label: "Loyalty",     icon: "fa-star",            description: "Loyalty points data from Square Loyalty API" },
];

// ─── Guide sections ───────────────────────────────────────────────────────────

function GuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Using Square Data in AI Tools</h3>
      <p className="mb-3">
        Square AI skills (<InlineCode>square_search_catalog</InlineCode>, <InlineCode>square_get_order</InlineCode>, etc.)
        work without any mapping configuration. When an AI workflow node runs, it calls the Square API at runtime
        and receives the full raw JSON response — the AI model then extracts the relevant details to answer
        the visitor's question.
      </p>
      <CodeBlock>{`// What the AI receives internally (not shown to visitor)
{
  "orders": [{
    "id": "CAISENgvlL...",
    "state": "COMPLETED",
    "line_items": [
      { "name": "Blue Jacket", "quantity": "1", "base_price_money": { "amount": 4999, "currency": "USD" } }
    ],
    "total_money": { "amount": 4999, "currency": "USD" }
  }]
}

// What the AI says to the visitor
"Your most recent order was for a Blue Jacket ($49.99),
 placed on March 20th — status: Completed. Is there
 anything else I can help you with?"`}</CodeBlock>

      <InfoBox>
        <strong>No configuration needed.</strong> AI tools are activated automatically when Square is connected.
        The AI decides which Square endpoints to call based on the visitor's question — no field mapping required.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-2">Comparison: AI skill vs workflow variable</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs mt-2">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Capability</th>
              <th className="px-3 py-2 text-left">AI Skill</th>
              <th className="px-3 py-2 text-left">Workflow Variable</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            <tr>
              <td className="px-3 py-2">Visitor-facing AI answers</td>
              <td className="px-3 py-2 text-success-green-700">Yes — reads live data</td>
              <td className="px-3 py-2 text-slate-400">No</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">HTTP POST / webhook injection</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes — use {"{square.scope.field}"}</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Requires Square to be connected</td>
              <td className="px-3 py-2 text-success-green-700">Yes (auto-activates)</td>
              <td className="px-3 py-2 text-success-green-700">Yes (populated at runtime)</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Requires this field reference page</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes — to know the variable name</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GuideWorkflowVars() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Using Square Data in Workflows</h3>
      <p className="mb-3">
        In workflow nodes that collect Square data (via HTTP Request nodes calling Square endpoints),
        the response fields can be injected into subsequent messages or outbound API calls using
        the variable syntax <InlineCode>{"{square.scope.field}"}</InlineCode>.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">Variable syntax</h4>
      <CodeBlock>{`// Scope: order  →  Field: state
{square.order.state}

// Scope: customer  →  Field: email_address
{square.customer.email_address}

// Scope: loyalty  →  Field: balance
{square.loyalty.balance}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Order confirmation message</h4>
      <CodeBlock>{`// Send message node — after order lookup
"Thanks {square.customer.given_name}! Your order
 {square.order.id} is currently {square.order.state}.
 Total paid: {square.order.total_money}."`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Loyalty check → ticket system</h4>
      <CodeBlock>{`// HTTP POST node — send loyalty data to your CRM
{
  "customer_email":    "{square.customer.email_address}",
  "loyalty_points":    "{square.loyalty.balance}",
  "lifetime_points":   "{square.loyalty.lifetime_points}",
  "customer_since":    "{square.customer.created_at}",
  "conversation_id":   "{conversation.id}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Arrays need AI to interpret.</strong> Fields like <InlineCode>line_items</InlineCode> and{" "}
        <InlineCode>variations</InlineCode> are arrays — use an AI node to summarize them naturally,
        rather than trying to index into them with a variable.
      </InfoBox>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type GuideTab = "ai-tools" | "workflow-vars";

export default function SquareDataMapping() {
  const [activeScope, setActiveScope] = useState<ScopeId>("booking");
  const [guideTab, setGuideTab]       = useState<GuideTab>("ai-tools");
  const [showGuide, setShowGuide]     = useState(false);

  const scope = SCOPES.find((s) => s.id === activeScope)!;

  const fieldsMap: Record<ScopeId, FieldDef[]> = {
    booking:  BOOKING_FIELDS,
    customer: CUSTOMER_FIELDS,
    order:    ORDER_FIELDS,
    catalog:  CATALOG_FIELDS,
    loyalty:  LOYALTY_FIELDS,
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">
            Reference guide for Square data fields. Use these variable paths in workflow nodes and HTTP POST payloads.
            AI skills do not require any mapping — they read raw data automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowGuide(!showGuide)}
          aria-expanded={showGuide}
          aria-controls="square-mapping-guide"
          className="shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-primary bg-primary-bg rounded-lg hover:bg-primary-bg transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
        >
          {showGuide ? "Hide Guide" : "How to Use This ▾"}
        </button>
      </div>

      {/* Expandable guide */}
      {showGuide && (
        <div id="square-mapping-guide" className="bg-white rounded-lg border border-slate-200">
          <div className="flex border-b border-slate-200">
            {(["ai-tools", "workflow-vars"] as GuideTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setGuideTab(tab)}
                className={`px-4 py-2.5 text-xs font-medium transition-colors ${
                  guideTab === tab
                    ? "border-b-2 border-primary text-primary-active"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab === "ai-tools" ? "AI Tools (no config needed)" : "Workflow Variables"}
              </button>
            ))}
          </div>
          <div className="p-5 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[420px]">
            {guideTab === "ai-tools"      && <GuideAiTools />}
            {guideTab === "workflow-vars" && <GuideWorkflowVars />}
          </div>
        </div>
      )}

      {/* Scope selector tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Data scope</div>
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveScope(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeScope === s.id
                    ? "bg-black text-white shadow-sm"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                }`}
              >
                <i className={`fa-solid ${s.icon} text-[10px]`} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scope header */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
            <i className={`fa-solid ${scope.icon} text-white text-sm`} />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">{scope.label}</div>
            <div className="text-xs text-slate-500">{scope.description}</div>
          </div>
          <div className="ml-auto">
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
              {fieldsMap[activeScope].length} fields
            </span>
          </div>
        </div>

        {/* Field table */}
        <div className="p-4 overflow-x-auto">
          <FieldTable scope={activeScope} fields={fieldsMap[activeScope]} />

          {/* Scope-specific notes */}
          {activeScope === "order" && (
            <InfoBox>
              <strong>line_items</strong> is an array. Each element contains{" "}
              <InlineCode>name</InlineCode>, <InlineCode>quantity</InlineCode>, and{" "}
              <InlineCode>base_price_money</InlineCode> (amount in cents + currency code).
              Use an AI node to format line items naturally for visitors.
            </InfoBox>
          )}
          {activeScope === "catalog" && (
            <InfoBox>
              <strong>variations</strong> is an array of catalog item variations — each has{" "}
              <InlineCode>name</InlineCode> (e.g. "Size M"), <InlineCode>price_money</InlineCode>,
              and <InlineCode>sku</InlineCode>. The AI skill{" "}
              <InlineCode>square_get_catalog_item</InlineCode> includes related objects automatically.
            </InfoBox>
          )}
          {activeScope === "loyalty" && (
            <InfoBox>
              <strong>balance</strong> is in points, not currency. To show a dollar value,
              divide by your program's points-per-dollar ratio. The AI can do this calculation
              automatically when given the program configuration.
            </InfoBox>
          )}
          {activeScope === "booking" && (
            <InfoBox>
              Booking data is populated after a successful{" "}
              <InlineCode>POST /api/admin/Square/Book</InlineCode> call in your workflow.
              The <InlineCode>booking_id</InlineCode> field is used in{" "}
              <InlineCode>POST /api/admin/Square/Cancel</InlineCode> to cancel the appointment.
            </InfoBox>
          )}
        </div>
      </div>

      {/* Workflow variable quick reference */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-1">
          Quick reference — all variable paths
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Copy-paste these into HTTP POST nodes, Send Message text, or Ask Question default values.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SCOPES.map((s) => (
            <div key={s.id} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <i className={`fa-solid ${s.icon} text-slate-500 text-xs`} />
                <span className="text-xs font-semibold text-slate-700">{s.label}</span>
              </div>
              <div className="space-y-1">
                {fieldsMap[s.id].slice(0, 4).map((f) => (
                  <code key={f.field} className="block text-[10px] text-primary-active font-mono truncate">
                    {`{square.${s.id}.${f.field}}`}
                  </code>
                ))}
                {fieldsMap[s.id].length > 4 && (
                  <span className="text-[10px] text-slate-400">
                    + {fieldsMap[s.id].length - 4} more — select scope above
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
